import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, ArrowLeft, ArrowRight, Globe } from 'lucide-react';
import { realDroneInventory, countries } from '@/data/realDroneInventory';
import { formatPrice } from '@/utils/priceFormatter';
import Footer from '@/components/sections/Footer';

export default function CountryPage() {
  const { countrySlug } = useParams();
  const navigate = useNavigate();

  // Find country data
  const country = countries.find((c) => c.slug === countrySlug);
  
  // Filter listings by country
  const countryListings = realDroneInventory.filter((listing) => listing.country === countrySlug);

  // Set document title on mount and when country changes
  useEffect(() => {
    if (country) {
      document.title = `Drones For Sale in ${country.name} | Industrial UAVs | UAVs For Sale`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          `Browse ${country.count}+ industrial drones and UAVs for sale in ${country.name}. Find inspection drones, survey drones, agriculture drones, delivery drones and more from verified vendors.`
        );
      }
      
      // Add/update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `https://uavsforsale.com/country/${countrySlug}`;
      
      // Add structured data for SEO and AI
      let ldJson = document.querySelector('script[data-seo="country-page"]');
      if (!ldJson) {
        ldJson = document.createElement('script');
        ldJson.type = 'application/ld+json';
        ldJson.setAttribute('data-seo', 'country-page');
        document.head.appendChild(ldJson);
      }
      ldJson.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `Drones For Sale in ${country.name}`,
        "description": `Browse ${country.count}+ industrial drones and UAVs for sale in ${country.name}`,
        "url": `https://uavsforsale.com/country/${countrySlug}`,
        "mainEntity": {
          "@type": "ItemList",
          "name": `Industrial Drones in ${country.name}`,
          "numberOfItems": country.count,
          "itemListElement": countryListings.slice(0, 5).map((drone, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Product",
              "name": drone.model,
              "brand": { "@type": "Brand", "name": drone.brand },
              "offers": {
                "@type": "Offer",
                "price": drone.price,
                "priceCurrency": drone.currency,
                "availability": "https://schema.org/InStock"
              }
            }
          }))
        }
      });
    } else {
      document.title = 'Country Not Found | UAVs For Sale';
    }
    
    return () => {
      // Cleanup structured data on unmount
      const ldJson = document.querySelector('script[data-seo="country-page"]');
      if (ldJson) ldJson.remove();
    };
  }, [country, countrySlug, countryListings]);

  if (!country) {
    return (
      <div className="min-h-screen">
        <Header />
        <ManufacturerLogos />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Country Not Found</h1>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />
      
      {/* Hero section */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Back button */}
            <Button
              variant="ghost"
              className="mb-8"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Home
            </Button>

            {/* Country flag and name */}
            <div className="text-7xl mb-6 animate-fade-in">{country.flag}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 animate-slide-up">
              UAVs & Drones For Sale in{' '}
              <span className="text-gradient bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                {country.name}
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 animate-slide-up stagger-1">
              Browse {country.count}+ verified industrial UAV listings from trusted vendors in {country.name}
            </p>

            {/* Stats */}
            <div className="flex justify-center gap-8 animate-slide-up stagger-2">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{country.count}+</div>
                <div className="text-sm text-muted-foreground">Listings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent">{countryListings.length}</div>
                <div className="text-sm text-muted-foreground">Featured Now</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Listings section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Available Listings in {country.name}</h2>
            
            {countryListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {countryListings.map((listing, index) => (
                  <Card
                    key={listing.id}
                    className={`group overflow-hidden hover:shadow-xl transition-all duration-300 border-border hover:border-primary/50 flex flex-col animate-slide-up stagger-${(index % 6) + 1} cursor-pointer`}
                    onClick={() => navigate(`/drone/${listing.id}`)}
                  >
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden bg-muted">
                      <img
                        src={listing.image}
                        alt={`${listing.model} drone for sale in ${country.name}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        {listing.featured && (
                          <Badge className="bg-accent text-accent-foreground">Featured</Badge>
                        )}
                        <Badge variant="secondary">{listing.condition}</Badge>
                      </div>
                    </div>

                    <CardHeader className="flex-grow">
                      <CardTitle className="text-xl line-clamp-2 group-hover:text-primary transition-colors">
                        {listing.model}
                      </CardTitle>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {listing.location}
                      </div>
                      <CardDescription className="line-clamp-2 mt-2">
                        {listing.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent>
                      {/* Specs */}
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">
                          {listing.specs.flightTime}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {listing.specs.payload}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {listing.category}
                        </Badge>
                      </div>
                    </CardContent>

                    <CardFooter className="flex items-center justify-between pt-4 border-t mt-auto">
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          {formatPrice(listing.price, listing.currency)}
                        </div>
                        <div className="text-xs text-muted-foreground">{listing.vendor}</div>
                      </div>
                      <Button
                        size="sm"
                        className="group/btn"
                      >
                        View Details
                        <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Globe className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">No Listings Available Yet</h3>
                <p className="text-muted-foreground mb-6">
                  We&apos;re working on adding more listings for {country.name}. Check back soon!
                </p>
                <Button onClick={() => navigate('/marketplace')}>Browse All Drones</Button>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-gray">
            <h2 className="text-2xl font-bold mb-4">Buy Industrial Drones in {country.name}</h2>
            <p className="text-muted-foreground mb-4">
              UAVs For Sale is the leading marketplace for industrial and commercial drones in {country.name}. 
              Our platform connects professional buyers with verified vendors offering inspection drones, 
              surveying UAVs, agricultural spraying drones, cinematography platforms, and more.
            </p>
            <h3 className="text-xl font-semibold mb-3">Popular Drone Categories in {country.name}</h3>
            <ul className="text-muted-foreground space-y-2 mb-4">
              <li>• <strong>Inspection Drones</strong> - For infrastructure, solar panels, and building inspections</li>
              <li>• <strong>Survey & Mapping Drones</strong> - RTK/PPK enabled for precision surveying</li>
              <li>• <strong>Agriculture Drones</strong> - Spraying and crop monitoring UAVs</li>
              <li>• <strong>Thermal Inspection Drones</strong> - FLIR equipped for thermal imaging</li>
              <li>• <strong>Delivery Drones</strong> - Last-mile logistics and medical delivery</li>
              <li>• <strong>Heavy Lift Drones</strong> - For cargo transport and construction</li>
            </ul>
            <h3 className="text-xl font-semibold mb-3">Top Drone Brands Available</h3>
            <p className="text-muted-foreground">
              We offer drones from leading manufacturers including DJI, Parrot, Autel Robotics, Skydio, 
              Freefly, Wingtra, senseFly, Yuneec, and more. All vendors are verified and listings are 
              reviewed by our expert broker team.
            </p>
          </div>
        </div>
      </section>

      {/* Other countries */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Browse Drones in Other Countries</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {countries
                .filter((c) => c.slug !== countrySlug)
                .slice(0, 6)
                .map((c) => (
                  <Card
                    key={c.slug}
                    className="group p-4 hover:shadow-lg hover:border-primary/50 cursor-pointer transition-all"
                    onClick={() => navigate(`/country/${c.slug}`)}
                  >
                    <div className="text-center space-y-2">
                      <div className="text-3xl">{c.flag}</div>
                      <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">
                        {c.name}
                      </h3>
                      <p className="text-xs text-muted-foreground">{c.count}+ drones</p>
                    </div>
                  </Card>
                ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
